/**
 * Serviço unificado para upload de imagens
 * Envia imagens ao armazenamento da Hostinger.
 */

import { getAccessToken } from './authApi';

const HOSTINGER_API_URL = 'https://nutrisync.dgapp.com.br/api/upload-image.php';
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 segundo

export interface UploadResponse {
  success: boolean;
  url?: string;
  error?: string;
  source?: 'hostinger';
}

/**
 * Aguarda um tempo específico (para retry com backoff)
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Testa a conectividade com a API do Hostinger
 */
export async function testHostingerAPI(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 segundos timeout

    const response = await fetch(HOSTINGER_API_URL, {
      method: 'OPTIONS',
      headers: {
        'Accept': 'application/json',
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    return response.ok || response.status === 200;
  } catch (error) {
    console.warn('[ImageUpload] Teste de conectividade com Hostinger falhou:', error);
    return false;
  }
}

/**
 * Faz upload de uma imagem para o Hostinger
 */
async function uploadToHostinger(
  file: File,
  userId: string,
  folder: string = 'community',
  retryCount: number = 0
): Promise<string> {
  try {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('userId', userId);
    formData.append('folder', folder);

    console.log(`[ImageUpload] Hostinger - Tentativa ${retryCount + 1}/${MAX_RETRIES + 1}`);
    console.log(`[ImageUpload] Arquivo: ${file.name}, Tamanho: ${file.size} bytes`);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos timeout

    const accessToken = getAccessToken();
    const response = await fetch(HOSTINGER_API_URL, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const responseText = await response.text();
    const contentType = response.headers.get('content-type') || '';

    console.log(`[ImageUpload] Hostinger - Status: ${response.status}, Content-Type: ${contentType}`);

    if (!response.ok) {
      // Se for erro de servidor (500) e ainda temos retries, tentar novamente
      if (response.status === 500 && retryCount < MAX_RETRIES) {
        const delayMs = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
        console.log(`[ImageUpload] Hostinger - Erro 500, tentando novamente em ${delayMs}ms...`);
        await delay(delayMs);
        return uploadToHostinger(file, userId, folder, retryCount + 1);
      }
      
      throw new Error(`HTTP ${response.status}: ${responseText.substring(0, 200)}`);
    }

    // Verificar se retornou HTML em vez de JSON
    if (responseText.trim().startsWith('<') || !contentType.includes('application/json')) {
      console.error('[ImageUpload] Hostinger retornou HTML em vez de JSON');
      throw new Error('Servidor retornou resposta inválida');
    }

    let result: UploadResponse;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error('[ImageUpload] Erro ao parsear JSON:', responseText.substring(0, 200));
      throw new Error('Resposta do servidor inválida');
    }

    if (!result.success || !result.url) {
      throw new Error(result.error || 'Upload falhou sem URL');
    }

    console.log(`[ImageUpload] Hostinger - Upload bem-sucedido: ${result.url}`);
    return result.url;
  } catch (error: any) {
    // Se for erro de rede/timeout e ainda temos retries, tentar novamente
    const isNetworkError = 
      error.name === 'AbortError' ||
      error.message?.includes('Network') || 
      error.message?.includes('timeout') ||
      error.message?.includes('fetch');

    if (isNetworkError && retryCount < MAX_RETRIES) {
      const delayMs = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
      console.log(`[ImageUpload] Hostinger - Erro de rede, tentando novamente em ${delayMs}ms...`);
      await delay(delayMs);
      return uploadToHostinger(file, userId, folder, retryCount + 1);
    }
    
    console.error('[ImageUpload] Hostinger - Erro final:', error);
    throw error;
  }
}

/**
 * Upload de imagem para a Hostinger.
 * 
 * @param file - Arquivo de imagem a ser enviado
 * @param userId - ID do usuário que está fazendo o upload
 * @param folder - Pasta de destino (padrão: 'community')
 * @returns URL da imagem armazenada na Hostinger
 */
export async function uploadImage(
  file: File,
  userId: string,
  folder: string = 'community'
): Promise<UploadResponse> {
  // Validar arquivo
  if (!file) {
    throw new Error('Nenhum arquivo fornecido');
  }

  if (!file.type.startsWith('image/')) {
    throw new Error('Arquivo deve ser uma imagem');
  }

  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    throw new Error('Imagem deve ter no máximo 5MB');
  }

  console.log(`[ImageUpload] Iniciando upload: ${file.name} (${file.size} bytes)`);

  const url = await uploadToHostinger(file, userId, folder);
  return { success: true, url, source: 'hostinger' };
}

/**
 * Converte Data URL (base64) para File
 */
export function dataURLtoFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new File([u8arr], filename, { type: mime });
}
